/**
 * The avatar picker's facets — World #2125 part C, as a Harry package emits them.
 *
 * World's first-login picker filters the roster on PREFIXED tags:
 * `presentation:<v>` (several), `kind:<v>`, `style:<v>`, `family:<v>`
 * (several). Allen reviewed all 126 rows on 2026-09-30 and that review lives
 * ONLY in the prod manifest rows, so a repackage that wrote `choices.tags`
 * verbatim would silently strip it. Two rules here, both pure:
 *
 *  - EXPANSION: the author's `pickerFacets` (and any prefixed tag typed into
 *    the free tags) become the record's prefixed tags, deduped; every other
 *    author tag is kept as typed.
 *  - CARRY-OVER: World's `carryPickerFacets` (scripts/ingest-avatar-package.mjs,
 *    PR #2149), applied at compose: per prefix, the author's value wins when
 *    set, otherwise the REPLACED row's tags of that prefix carry. Legacy
 *    unprefixed tags (`male`, `fantasy`) are not carried. Because Harry's
 *    record then already holds the carried tags, World's ingest is a no-op on
 *    it — and still catches what Harry could not see (an unreachable roster).
 *
 * The record carries the facets AS TAGS only: World's roster schema is strict
 * (`additionalProperties:false`, "World amends first, Harry emits second"), so
 * a `pickerFacets` field on the record would refuse at ingest.
 * `pickerFacetsFromTags` reads them back.
 *
 * Depends on: session/document (the schema and type). Depended on by:
 * session/package (compose), session/roster-sheet (the batch columns), the
 * app's Package panel.
 */
import type { PickerFacets } from './document.js';

/** World's prefixes, in World's order (`PICKER_FACET_PREFIXES`, PR #2149). */
export const PICKER_FACET_PREFIXES = ['presentation:', 'kind:', 'style:', 'family:'] as const;
export type PickerFacetKey = 'presentation' | 'kind' | 'style' | 'family';
export const PICKER_FACET_KEYS: readonly PickerFacetKey[] = ['presentation', 'kind', 'style', 'family'];

/** World's closed vocabularies (scripts/avatar-tags/avatar-tags-2026-09-30.json + the card's filters). */
export const PICKER_PRESENTATIONS: readonly string[] = ['masculine', 'feminine', 'androgynous'];
export const PICKER_KINDS: readonly string[] = ['human', 'humanoid', 'animal', 'other'];
export const PICKER_STYLES: readonly string[] = ['realistic', 'stylized'];
/**
 * The families in World's reviewed table, 2026-09-30. OPEN: World builds a
 * chip per family it finds, so a new one is legal — it is only WARNED, in
 * case it is a misspelling that would mint a chip of one.
 */
export const PICKER_FAMILIES: readonly string[] = [
  'anthropomorphs', 'atlanteans', 'barbarians', 'beach', 'canine', 'clubkids', 'costumed', 'dolls', 'fae',
  'feline', 'pirates', 'reptilian', 'rodent', 'seabees', 'shaggy', 'vroids', 'warriors',
];

const prefixOf = (key: PickerFacetKey): string => `${key}:`;
const isPickerTag = (t: string): boolean => PICKER_FACET_PREFIXES.some((p) => t.startsWith(p));
const uniq = (xs: readonly string[]): string[] => [...new Set(xs)];

/** The values of one prefix among some tags, deduped, in tag order. */
function valuesOf(tags: readonly string[], key: PickerFacetKey): string[] {
  const p = prefixOf(key);
  return uniq(tags.filter((t) => t.startsWith(p)).map((t) => t.slice(p.length)).filter((v) => v !== ''));
}

function isSet(f: PickerFacets, key: PickerFacetKey): boolean {
  return f[key] !== undefined;
}

/** Read the facets out of a tag list (a roster row's, a record's). A second `kind:`/`style:` tag is dropped — World's table has one. */
export function pickerFacetsFromTags(tags: readonly string[]): PickerFacets {
  const out: PickerFacets = {};
  const presentation = valuesOf(tags, 'presentation');
  const kind = valuesOf(tags, 'kind');
  const style = valuesOf(tags, 'style');
  const family = valuesOf(tags, 'family');
  if (presentation.length > 0) out.presentation = presentation;
  if (kind.length > 0) out.kind = kind[0]!;
  if (style.length > 0) out.style = style[0]!;
  if (family.length > 0) out.family = family;
  return out;
}

/** The facets as World's tags, in apply-facet-tags.mjs order: presentation…, kind, style, family…. */
export function pickerFacetTags(f: PickerFacets): string[] {
  return uniq([
    ...(f.presentation ?? []).map((v) => `presentation:${v}`),
    ...(f.kind !== undefined ? [`kind:${f.kind}`] : []),
    ...(f.style !== undefined ? [`style:${f.style}`] : []),
    ...(f.family ?? []).map((v) => `family:${v}`),
  ]);
}

export interface PickerFacetIssues {
  /** A closed vocabulary broken — World's filters would never match it. */
  readonly refusals: readonly string[];
  /** A new family — legal (World mints a chip), said in case it is a typo. */
  readonly warnings: readonly string[];
}

/** presentation / kind / style against World's closed lists; family against the known table (warning only). */
export function pickerFacetIssues(f: PickerFacets): PickerFacetIssues {
  const refusals: string[] = [];
  const warnings: string[] = [];
  for (const v of f.presentation ?? []) {
    if (!PICKER_PRESENTATIONS.includes(v)) refusals.push(`picker presentation "${v}" is not one of ${PICKER_PRESENTATIONS.join(', ')} — World's avatar picker filters on exactly these`);
  }
  if (f.kind !== undefined && !PICKER_KINDS.includes(f.kind)) refusals.push(`picker kind "${f.kind}" is not one of ${PICKER_KINDS.join(', ')}`);
  if (f.style !== undefined && !PICKER_STYLES.includes(f.style)) refusals.push(`picker style "${f.style}" is not one of ${PICKER_STYLES.join(', ')}`);
  for (const v of f.family ?? []) {
    if (!PICKER_FAMILIES.includes(v)) warnings.push(`picker family "${v}" is new — World will show it as its own chip; check the spelling`);
  }
  return { refusals, warnings };
}

export interface ResolvedPickerFacets {
  /** What the record's tags say, per facet — authored or carried. */
  readonly facets: PickerFacets;
  /** The record's tags: the author's unprefixed tags as typed, then the facets expanded. */
  readonly tags: readonly string[];
  /** The facets that came from the replaced row rather than the author. */
  readonly carried: readonly PickerFacetKey[];
  /** Refusals on what the AUTHOR set; a carried value is World's own and only warned. */
  readonly refusals: readonly string[];
  readonly warnings: readonly string[];
}

/**
 * The author's choices + the row this package REPLACES (its tags; null = a
 * new row or no roster read) → the record's facets and tags. Per facet: the
 * author's value when set (the `pickerFacets` field, else a prefixed tag typed
 * into the free tags), otherwise the replaced row's tags of that prefix.
 */
export function resolvePickerFacets(
  choices: { readonly tags?: readonly string[] | undefined; readonly pickerFacets?: PickerFacets | undefined },
  replacedTags: readonly string[] | null,
): ResolvedPickerFacets {
  const typed = choices.tags ?? [];
  const fromTyped = pickerFacetsFromTags(typed);
  const authored: PickerFacets = { ...fromTyped, ...(choices.pickerFacets ?? {}) };
  const previous = pickerFacetsFromTags(replacedTags ?? []);
  const facets: PickerFacets = {};
  const carried: PickerFacetKey[] = [];
  for (const key of PICKER_FACET_KEYS) {
    const from = isSet(authored, key) ? authored : isSet(previous, key) ? previous : null;
    if (from === null) continue;
    if (from === previous) carried.push(key);
    (facets as Record<PickerFacetKey, unknown>)[key] = from[key];
  }
  const own = pickerFacetIssues(authored);
  // Two `kind:`/`style:` tags TYPED by the author is a choice left unmade — refused, never silently
  // cut to the first (Codex P2 on #231). Moot when the picker field sets that facet itself.
  const ambiguous: string[] = [];
  for (const key of ['kind', 'style'] as const) {
    const typedValues = valuesOf(typed, key);
    if (typedValues.length > 1 && choices.pickerFacets?.[key] === undefined) {
      ambiguous.push(`the tags name ${typedValues.length} picker ${key}s (${typedValues.map((v) => `${key}:${v}`).join(', ')}) — World's picker has one ${key}; keep one`);
    }
  }
  // A bare `kind:` typed by the author is a malformed choice, refused — not quietly an unset facet (Codex P2, #231).
  for (const t of typed) {
    if ((PICKER_FACET_PREFIXES as readonly string[]).includes(t)) ambiguous.push(`the tag "${t}" names no value — finish it or remove it`);
  }
  const kept: PickerFacets = {};
  for (const key of carried) (kept as Record<PickerFacetKey, unknown>)[key] = previous[key];
  const inherited = pickerFacetIssues(kept);
  const warnings = [...own.warnings, ...inherited.refusals.map((r) => `${r} (kept from the replaced roster row)`), ...inherited.warnings];
  for (const key of ['kind', 'style'] as const) {
    if (carried.includes(key) && valuesOf(replacedTags ?? [], key).length > 1) {
      warnings.push(`the replaced roster row has ${valuesOf(replacedTags ?? [], key).length} ${key}: tags — only "${previous[key]}" is kept`);
    }
  }
  return {
    facets,
    tags: uniq([...typed.filter((t) => !isPickerTag(t)), ...pickerFacetTags(facets)]),
    carried,
    refusals: [...own.refusals, ...ambiguous],
    warnings,
  };
}
